import { useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { useParams, useNavigate } from 'react-router-dom'
import { useWizard } from '@/components/common/wizard/WizardContext'
import { Button } from '@/components/ui/button'
import SubmitButton from '@/components/common/SubmitButton'
import { GET_AUDIENCES } from '@/graphql/audience-queries'
import { useAudienceDataWithHypotheses } from '@/hooks/useAudiencesWithHypotheses'
import { GET_AD_CREATIVES_WITH_ASSETS } from '@/graphql/ad-creatives-library-queries'
import { CREATE_AD_CAMPAIGN, ADD_AUDIENCE_TO_AD_CAMPAIGN } from '@/graphql/media-buying-queries'
import { UPDATE_AD_CAMPAIGN } from '@/graphql/ads/ad-campaigns-queries'
import { getAllStages, getAvailableStages } from '@/utils/ad-campaigns-helpers'
import useAdsSelection from '@/components/mediaBuying/adCampaign/screens/AdsSelection/hooks/useAdsSelection'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { showNotification } from '@/utils/notifications'
import { filterSupportedPlacements } from '@/utils/ad-campaign-constants'
import country from 'country-list-js'
import {
    intervalToDuration,
    formatDuration,
    isFuture,
    addDays,
    startOfDay,
    addHours,
    addMinutes,
    format,
} from 'date-fns'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'
import useAdsManagerStore from '@/store/adsManagerStore'
import useAudienceTargetingStore from '@/store/audienceTargetingStore'
import { generateAudienceDisplayName } from '../utils/audienceNameUtils'
import { getCampaignObjectiveLabel } from '@/utils/campaign-objectives'
import { getHypothesisMap, getLaunchAudienceErrorMessage, withHypothesisPayload } from '@/utils/audience-builder'
import { getLookalikeMap } from '@/utils/lookalike-audiences'
import { getAccountAudienceMap, withAccountAudiencePayload } from '@/utils/account-audiences'
import { getAudienceLocales, localesForSubmission } from '@/utils/ad-set-locales'
import { exclusionsForSubmission } from '@/utils/audience-exclusions'
import { BUDGET_MOVE_STEP, budgetMoveForSubmission, getBudgetMoveErrorMessage } from '@/utils/budget-move'
import {
    ADVANTAGE_LEADS_BUDGET_TYPE,
    campaignBudgetEditFields,
    isAdvantageLeadsBudget,
} from '@/utils/advantage-leads'
import {
    CAMPAIGN_BUDGET_TYPE,
    anyCampaignBudgetForSubmission,
    getCampaignBudgetErrorMessage,
    hasCampaignBudget,
    isPlainCampaignBudget,
    limitKey,
    limitsForSubmission,
    spendLimitEditFields,
} from '@/utils/campaign-budget'
import {
    WARMUP_OFFERED_STEP,
    WARMUP_STEP,
    describeWarmupProblem,
    eventChoiceForSubmission,
    getWarmupLaunchErrorMessage,
    maxDailyBudgetForSubmission,
    resolveLaunchEvent,
    warmupFeaturesOffered,
    warmupForSubmission,
} from '@/utils/pixel-warmup'

// Helper function to convert timezone offset to IANA timezone format
const getTimezoneFromOffset = (offset) => {
    // Note: Etc/GMT timezones have inverted signs
    // UTC+10 becomes Etc/GMT-10
    const sign = offset >= 0 ? '-' : '+'
    const absOffset = Math.abs(offset)
    return `Etc/GMT${sign}${absOffset}`
}

function useReviewLaunch(isAddAudience = false) {
    const lastUpdateWasAdSetOnlyRef = useRef(false)
    const [isFormSubmitting, setIsFormSubmitting] = useState(false)

    const {
        getStepContentData,
        setStepContentData,
        clearWizardStorage,
        registerCustomNavigation,
        clearCustomNavigation,
    } = useWizard()
    const { data: catalogueAudiencesData, loading: audiencesLoading } = useQuery(GET_AUDIENCES)
    const audiencesData = useAudienceDataWithHypotheses(catalogueAudiencesData, getStepContentData)
    const { connectedPublishers } = useMediaBuyingContext()
    const adsSelectionData = useAdsSelection()
    const audienceSelections = adsSelectionData?.audienceSelections || {}
    const getSelectedCreativesForAudience = adsSelectionData?.getSelectedCreativesForAudience
    const params = useParams()
    const campaignId = params.id
    const navigate = useNavigate()
    const { addProcessingCampaign } = useAdsManagerStore()
    const { clearAllSettings } = useAudienceTargetingStore()

    const [collapsedStages, setCollapsedStages] = useState(new Set())
    const [selectedAudience, setSelectedAudience] = useState(null)

    // Ad Campaign mutation
    const [createAdCampaign, { loading: isSubmittingCreate }] = useMutation(CREATE_AD_CAMPAIGN, {
        onCompleted: (data) => {
            const launchOption = getStepContentData('reviewLaunch')?.launchOption || 'launch_now'
            const isScheduled = launchOption !== 'launch_now'
            const message = `Campaign ${isScheduled ? 'scheduled' : 'created'} successfully!`

            showNotification(message, 'success', 3000, null, null, true, true)
            setTimeout(() => {
                clearWizardStorage()
            }, 0)

            setTimeout(() => {
                navigate(`/campaigns/media-buying/${campaignId}/ads-manager`)
            }, 50)
        },
        onError: (error) => {
            console.error('Error creating ad campaign:', error)
            const errorMessage =
                getLaunchAudienceErrorMessage(error) ||
                getCampaignBudgetErrorMessage(error) ||
                getWarmupLaunchErrorMessage(error) ||
                error.graphQLErrors?.[0]?.message ||
                'Failed to create campaign. Please try again.'
            showNotification(errorMessage, 'error', 3000, null, null, true, true)
        },
    })

    // Update Ad Campaign mutation (edit mode)
    const [updateAdCampaignMutation, { loading: isSubmittingUpdate }] = useMutation(UPDATE_AD_CAMPAIGN, {
        onCompleted: () => {
            const adSetOnly = lastUpdateWasAdSetOnlyRef.current
            lastUpdateWasAdSetOnlyRef.current = false
            showNotification(
                adSetOnly ? 'Ad set updated successfully!' : 'Campaign updated successfully!',
                'success',
                3000,
                null,
                null,
                true,
                true,
            )
            setTimeout(() => {
                clearWizardStorage()
            }, 0)
            setTimeout(() => {
                navigate(`/campaigns/media-buying/${campaignId}/ads-manager`)
            }, 50)
        },
        onError: (error) => {
            console.error('Error updating ad campaign:', error)
            const adSetOnly = lastUpdateWasAdSetOnlyRef.current
            lastUpdateWasAdSetOnlyRef.current = false
            const errorMessage =
                getCampaignBudgetErrorMessage(error) ||
                error.graphQLErrors?.[0]?.message ||
                (adSetOnly ? 'Failed to update ad set. Please try again.' : 'Failed to update campaign. Please try again.')
            // Some ad sets were saved and others were not: keep the list of failed ad sets on screen longer
            const isPartialFailure = error.graphQLErrors?.[0]?.extensions?.code === 'AD_SET_UPDATE_PARTIAL'
            showNotification(errorMessage, 'error', isPartialFailure ? 15000 : 3000, null, null, true, true)
        },
    })

    // Add Audience to Ad Campaign mutation
    const [addAudienceToAdCampaign, { loading: isSubmittingAddAudience }] = useMutation(ADD_AUDIENCE_TO_AD_CAMPAIGN, {
        onCompleted: () => {
            const launchOption = getStepContentData('reviewLaunch')?.launchOption || 'launch_now'
            const isScheduled = launchOption !== 'launch_now'
            const message = `Audience ${isScheduled ? 'scheduled' : 'added'} successfully!`

            showNotification(message, 'success', 3000, null, null, true, true)
            setTimeout(() => {
                clearWizardStorage()
            }, 0)

            setTimeout(() => {
                navigate(`/campaigns/media-buying/${campaignId}/ads-manager?skipInitialLoad=true`)
            }, 100)
        },
        onError: (error) => {
            console.error('Error adding audience to ad campaign:', error)
            const errorMessage =
                getLaunchAudienceErrorMessage(error) ||
                getCampaignBudgetErrorMessage(error) ||
                getBudgetMoveErrorMessage(error) ||
                error.graphQLErrors?.[0]?.message ||
                'Failed to add audience. Please try again.'
            showNotification(errorMessage, 'error', 3000, null, null, true, true)
        },
    })

    // Combined loading state (GraphQL in flight)
    const isMutationLoading = isSubmittingCreate || isSubmittingAddAudience || isSubmittingUpdate
    const isSubmitting = isFormSubmitting || isMutationLoading

    // Determine edit mode from prefetched wizard data
    const prefetchedData = useMemo(() => getStepContentData('prefetchedData') || {}, [getStepContentData])
    const isEditMode = prefetchedData.isEditMode === true
    const isEditAdSetMode = prefetchedData.isEditAdSetMode === true
    const editAdCampaignId = prefetchedData.adCampaignId
    const adSetMapping = prefetchedData.adSetMapping || {}

    // Get review launch data from wizard state
    const reviewLaunchData = useMemo(() => {
        const data = getStepContentData('reviewLaunch')
        // Calculate tomorrow's date as default for scheduling
        const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd')

        return {
            campaignName: data?.campaignName || '',
            launchOption: data?.launchOption || 'launch_now',
            launchDate: data?.launchDate || tomorrow,
            launchTime: data?.launchTime || '00:00',
            audienceNames: data?.audienceNames || {},
        }
    }, [getStepContentData])

    // Update wizard state
    const updateReviewLaunchData = useCallback(
        (updates) => {
            const newData = { ...reviewLaunchData, ...updates }
            setStepContentData(newData, 'reviewLaunch')
        },
        [reviewLaunchData, setStepContentData],
    )

    const audiences = useMemo(() => {
        return audiencesData?.Audience?.audiences || []
    }, [audiencesData])

    const allStages = useMemo(() => getAllStages(), [])

    // Get available stages based on campaign objective
    const availableStages = useMemo(() => {
        const objectiveData = getStepContentData('campaignObjective')
        const selectedObjective = objectiveData?.selectedObjective?.toLowerCase() ?? 'awareness'
        return getAvailableStages(selectedObjective)
    }, [getStepContentData])

    // Get all creative IDs from all audiences
    const allCreativeIds = useMemo(() => {
        if (!audienceSelections || !getSelectedCreativesForAudience) return []
        const ids = new Set()
        allStages.forEach((stage) => {
            const selectedIds = audienceSelections[stage.id] || []
            selectedIds.forEach((audienceId) => {
                const creativeIds = getSelectedCreativesForAudience(audienceId, stage.id) || []
                creativeIds.forEach((id) => ids.add(id))
            })
        })
        return Array.from(ids)
    }, [allStages, audienceSelections, getSelectedCreativesForAudience])

    // Fetch all creatives - we fetch all and filter on client side
    const { data: creativesData, loading: creativesLoading } = useQuery(GET_AD_CREATIVES_WITH_ASSETS, {
        variables: { searchTerm: null, formats: null },
        skip: allCreativeIds.length === 0,
    })

    // Get creative data by ID
    const getCreativeById = useCallback(
        (id) => {
            return creativesData?.AdCreative?.searchFilterAdCreativesJoinAssets?.find((c) => c.id === id)
        },
        [creativesData],
    )

    // Get creatives for a specific audience
    const getCreativesForAudience = useCallback(
        (audienceId, stageId) => {
            if (!getSelectedCreativesForAudience) return []
            const creativeIds = getSelectedCreativesForAudience(audienceId, stageId) || []
            return creativeIds.map((id) => getCreativeById(id)).filter(Boolean)
        },
        [getSelectedCreativesForAudience, getCreativeById],
    )

    // Whether the campaign has one campaign budget (Advantage+ leads or the plain campaign budget) instead of audience budgets
    const usesCampaignBudget = useCallback(() => {
        if (hasCampaignBudget(getStepContentData('budgetSchedule'))) return true
        const mode = (getStepContentData('prefetchedData') || {}).campaignBudgetMode
        return isAddAudience && (mode === ADVANTAGE_LEADS_BUDGET_TYPE || mode === CAMPAIGN_BUDGET_TYPE)
    }, [getStepContentData, isAddAudience])

    // Only Advantage+ leads chooses placements and devices itself; the plain campaign budget keeps the owner's
    const usesAutomaticPlacements = useCallback(() => {
        if (isAdvantageLeadsBudget(getStepContentData('budgetSchedule'))) return true
        return (
            isAddAudience &&
            (getStepContentData('prefetchedData') || {}).campaignBudgetMode === ADVANTAGE_LEADS_BUDGET_TYPE
        )
    }, [getStepContentData, isAddAudience])

    // The daily spend limits typed for an audience, in minor units; null for a campaign without limits
    const getAudienceLimits = useCallback(
        (stageId, audienceId) => {
            const budgetData = getStepContentData('budgetSchedule')
            const plain =
                isPlainCampaignBudget(budgetData) ||
                (isAddAudience && (getStepContentData('prefetchedData') || {}).campaignBudgetMode === CAMPAIGN_BUDGET_TYPE)
            if (!plain) return null
            return limitsForSubmission(budgetData)[limitKey(stageId, audienceId)] || null
        },
        [getStepContentData, isAddAudience],
    )

    // Whether the audience still takes part in the budget: one unselected on the Smart budget screen has no share, is not listed for review and is not launched
    const isAudienceBudgeted = useCallback(
        (stageId, audienceId) => {
            const budgetData = getStepContentData('budgetSchedule')
            if (!budgetData || usesCampaignBudget() || budgetData.budgetType !== 'smart') return true
            return !!budgetData.audienceSelections?.[stageId]?.audiences?.[audienceId]
        },
        [getStepContentData, usesCampaignBudget],
    )

    // Get selected audiences for a stage
    const getSelectedAudiences = useCallback(
        (stageId) => {
            if (!audienceSelections) return []
            const selectedIds = audienceSelections[stageId] || []
            return audiences.filter(
                (audience) => selectedIds.includes(audience.id) && isAudienceBudgeted(stageId, audience.id),
            )
        },
        [audienceSelections, audiences, isAudienceBudgeted],
    )

    // Get visible stages (stages that have selected audiences - regardless of availability)
    const visibleStages = useMemo(() => {
        return allStages.filter((stage) => getSelectedAudiences(stage.id).length > 0)
    }, [allStages, getSelectedAudiences])

    // Handle stage collapse/expand
    const toggleStageCollapse = useCallback((stageId) => {
        setCollapsedStages((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(stageId)) {
                newSet.delete(stageId)
            } else {
                newSet.add(stageId)
            }
            return newSet
        })
    }, [])

    // Check if all visible stages are collapsed
    const areAllStagesCollapsed = useMemo(() => {
        return visibleStages.length > 0 && visibleStages.every((stage) => collapsedStages.has(stage.id))
    }, [visibleStages, collapsedStages])

    // Handle toggle all stages
    const toggleAllStages = useCallback(() => {
        if (areAllStagesCollapsed) {
            setCollapsedStages(new Set())
        } else {
            setCollapsedStages(new Set(visibleStages.map((stage) => stage.id)))
        }
    }, [areAllStagesCollapsed, visibleStages])

    // Handle audience selection
    const handleAudienceSelection = useCallback(
        (stageId, audienceId) => {
            const audience = audiences.find((a) => a.id === audienceId)
            const stage = allStages.find((s) => s.id === stageId)

            if (audience && stage) {
                setSelectedAudience({ audience, stage })
            }
        },
        [audiences, allStages],
    )

    // Handle audience name change
    const handleAudienceNameChange = useCallback(
        (stageId, audienceId, newName) => {
            const key = `${stageId}_${audienceId}`
            updateReviewLaunchData({
                audienceNames: {
                    ...reviewLaunchData.audienceNames,
                    [key]: newName,
                },
            })
        },
        [reviewLaunchData.audienceNames, updateReviewLaunchData],
    )

    // Get audience budget from budget schedule wizard data; null when the campaign budget funds the audience
    const getAudienceBudget = useCallback(
        (stageId, audienceId) => {
            const budgetData = getStepContentData('budgetSchedule')
            if (usesCampaignBudget()) return null
            if (!budgetData) return 0

            const { budgetType, audienceSelections, manualBudgets } = budgetData

            if (budgetType === 'smart') {
                const amount = audienceSelections[stageId]?.audiences?.[audienceId]?.amount
                return parseFloat(amount) || 0
            } else {
                const amount = manualBudgets[stageId]?.audiences?.[audienceId]?.amount
                return parseFloat(amount) || 0
            }
        },
        [getStepContentData, usesCampaignBudget],
    )

    // Get campaign objective
    const getCampaignObjective = useCallback(() => {
        const objectiveData = getStepContentData('campaignObjective')
        const objectiveId = objectiveData?.selectedObjective || 'awareness'
        return getCampaignObjectiveLabel(objectiveId)
    }, [getStepContentData])

    // Get gender targets for an audience
    const getAudienceGenderTargets = useCallback(
        (stageId, audienceId) => {
            const audienceTargetingData = getStepContentData('audienceTargeting')
            const stageSettings = audienceTargetingData?.settings?.[stageId]

            if (stageSettings?.[audienceId]?.gender_targets) {
                return stageSettings[audienceId].gender_targets
            }

            // Default to both genders if not set
            return ['male', 'female']
        },
        [getStepContentData],
    )

    // Get age targets for an audience
    const getAudienceAgeTargets = useCallback(
        (stageId, audienceId) => {
            const audienceTargetingData = getStepContentData('audienceTargeting')
            const stageSettings = audienceTargetingData?.settings?.[stageId]

            if (stageSettings?.[audienceId]?.age_targets) {
                return stageSettings[audienceId].age_targets
            }

            // Default to all age ranges if not set
            return ['18-24', '25-34', '35-44', '45-54', '55-64', '65+']
        },
        [getStepContentData],
    )

    // Get platform placement targets for an audience
    const getAudiencePlatformPlacementTargets = useCallback(
        (stageId, audienceId) => {
            const audienceTargetingData = getStepContentData('audienceTargeting')
            const stageSettings = audienceTargetingData?.settings?.[stageId]

            if (stageSettings?.[audienceId]?.platform_placement_targets) {
                return filterSupportedPlacements(stageSettings[audienceId].platform_placement_targets)
            }

            // Return empty array if not set (don't assume defaults)
            return []
        },
        [getStepContentData],
    )

    // Get device targets for an audience
    const getAudienceDeviceTargets = useCallback(
        (stageId, audienceId) => {
            const audienceTargetingData = getStepContentData('audienceTargeting')
            const stageSettings = audienceTargetingData?.settings?.[stageId]

            if (stageSettings?.[audienceId]?.device_targets) {
                return stageSettings?.[audienceId]?.device_targets
            }

            // Default to all devices if not set
            return ['desktop', 'mobile_ios', 'mobile_android']
        },
        [getStepContentData],
    )

    // Get location data for an audience
    const getAudienceLocationData = useCallback(
        (stageId, audienceId) => {
            const audienceTargetingData = getStepContentData('audienceTargeting')
            const locationTargetingData = getStepContentData('locationTargeting')
            const stageSettings = audienceTargetingData?.settings?.[stageId]

            // Find the audience settings object that contains 'countries' property
            let countries = null
            if (stageSettings) {
                // Check direct audience ID key first
                if (stageSettings[audienceId]?.countries) {
                    countries = stageSettings[audienceId].countries
                } else {
                    // Search through all keys to find the one with countries data
                    for (const key in stageSettings) {
                        if (key.includes(audienceId) && stageSettings[key]?.countries) {
                            countries = stageSettings[key].countries
                            break
                        }
                    }
                }
            }

            if (!countries) {
                return {
                    target: locationTargetingData?.selectedLocation,
                    included: [],
                    excluded: [],
                }
            }

            // Create a map of country codes to names for quick lookup
            const countryMap = new Map()
            Object.values(country.all).forEach((c) => {
                countryMap.set(c.iso2, c.name)
            })

            // Convert country codes to names
            const included = (countries.included || []).map((code) => {
                const name = countryMap.get(code)
                return name || code // Fallback to code if name not found
            })

            const excluded = (countries.excluded || []).map((code) => {
                const name = countryMap.get(code)
                return name || code // Fallback to code if name not found
            })

            return {
                target: locationTargetingData?.selectedLocation,
                included,
                excluded,
            }
        },
        [getStepContentData],
    )

    // Get custom event type for an audience
    const getAudienceCustomEventType = useCallback(
        (stageId, audienceId) => {
            const audienceTargetingData = getStepContentData('audienceTargeting')
            const stageSettings = audienceTargetingData?.settings?.[stageId]

            if (stageSettings) {
                // Check direct audience ID key first
                if (stageSettings[audienceId]?.custom_event_type) {
                    return stageSettings[audienceId].custom_event_type
                } else {
                    // Search through all keys to find the one with custom_event_type data
                    for (const key in stageSettings) {
                        if (key.includes(audienceId) && stageSettings[key]?.custom_event_type) {
                            return stageSettings[key].custom_event_type
                        }
                    }
                }
            }

            return null
        },
        [getStepContentData],
    )

    // The event choice the user made for an audience: 'automatic' only after they picked it, else null
    const getAudienceEventChoice = useCallback(
        (stageId, audienceId) => {
            const stageSettings = getStepContentData('audienceTargeting')?.settings?.[stageId]
            return eventChoiceForSubmission(stageSettings?.[audienceId])
        },
        [getStepContentData],
    )

    // The event an audience launches with. "Automatic" counts only while the warm-up features are offered (the same gate
    // as the launch request); when the pick is dropped, the objective's default event is launched, which is the event
    // the Automatic note named, so the audience step and the review never imply another event than the one sent.
    const getAudienceLaunchEvent = useCallback(
        (stageId, audienceId) => {
            const objective = String(getStepContentData('campaignObjective')?.selectedObjective || '').toLowerCase()
            return resolveLaunchEvent({
                eventChoice: getAudienceEventChoice(stageId, audienceId),
                stored: getAudienceCustomEventType(stageId, audienceId),
                objective,
                offered: warmupFeaturesOffered({
                    objective,
                    offeredStep: getStepContentData(WARMUP_OFFERED_STEP),
                    isAddAudience,
                    isEditMode,
                    campaignBudget: !!anyCampaignBudgetForSubmission(getStepContentData('budgetSchedule')),
                }),
                warmup: getStepContentData(WARMUP_STEP) || {},
            })
        },
        [getAudienceCustomEventType, getAudienceEventChoice, getStepContentData, isAddAudience, isEditMode],
    )

    // Get audience display name with dynamic suffix generation
    const getAudienceDisplayName = useCallback(
        (stageId, audienceId, defaultName) => {
            return generateAudienceDisplayName({
                stageId,
                audienceId,
                defaultName,
                reviewLaunchData,
                getStepContentData,
                allStages,
                connectedPublishers,
                getAudienceGenderTargets,
                getAudienceAgeTargets,
                getAudiencePlatformPlacementTargets,
            })
        },

        [
            reviewLaunchData,
            getStepContentData,
            allStages,
            connectedPublishers,
            getAudienceGenderTargets,
            getAudienceAgeTargets,
            getAudiencePlatformPlacementTargets,
        ],
    )

    // Get recency days for an audience (Facebook Fans)
    const getAudienceRecencyDays = useCallback(
        (stageId, audienceId) => {
            const audienceTargetingData = getStepContentData('audienceTargeting')
            const stageSettings = audienceTargetingData?.settings?.[stageId]

            if (stageSettings) {
                // Check direct audience ID key first
                if (stageSettings[audienceId]?.recency_days !== undefined) {
                    return stageSettings[audienceId].recency_days
                } else {
                    // Search through all keys to find the one with recency_days data
                    for (const key in stageSettings) {
                        if (key.includes(audienceId) && stageSettings[key]?.recency_days !== undefined) {
                            return stageSettings[key].recency_days
                        }
                    }
                }
            }

            return null
        },
        [getStepContentData],
    )

    // Validation/Submit handler for wizard
    const validateForm = useCallback(async () => {
        if (!campaignId) {
            showNotification('Campaign ID is missing. Please reload and try again.', 'error', 3000, null, null, true, true)
            return false
        }
        if (!connectedPublishers || Object.keys(connectedPublishers).length === 0) {
            showNotification(
                'A connected Ad Account is required. Connect an account in Media Settings, then try again.',
                'error',
                5000,
                null,
                null,
                true,
                true,
            )
            return false
        }

        const editPrefetched = getStepContentData('prefetchedData') || {}
        const isAdSetOnlyEdit = editPrefetched.isEditAdSetMode === true

        // Validate campaign name is not empty (ad set edit does not change campaign name)
        const currentReviewLaunchData = getStepContentData('reviewLaunch') || {}
        const campaignName = currentReviewLaunchData.campaignName?.trim()

        if (!isAdSetOnlyEdit && !campaignName) {
            showNotification('Campaign name is required', 'error', 3000, null, null, true, true)
            return false
        }

        try {
            // Get publisher ID (first connected publisher)
            const publisherId = Object.keys(connectedPublishers)[0]

            // Get wizard data
            const campaignObjectiveData = getStepContentData('campaignObjective') || {}
            const locationTargetingData = getStepContentData('locationTargeting') || {}
            const salesFunnelData = getStepContentData('salesFunnelSetup') || {}
            const reviewLaunchData = getStepContentData('reviewLaunch') || {}

            // Get publisher timezone information
            const publisherData = connectedPublishers[publisherId]
            const timezoneOffset = publisherData?.timezone_offset || 0 // Offset in minutes

            // Build launch type and schedule time
            const launchType =
                reviewLaunchData.launchOption === 'launch_now'
                    ? 'immediate'
                    : reviewLaunchData.launchOption === 'launch_tonight'
                      ? 'midnight'
                      : 'schedule'
            let scheduleTime = null

            if (launchType !== 'immediate') {
                if (reviewLaunchData.launchOption === 'launch_tonight') {
                    // For midnight launch, calculate next midnight in account timezone using date-fns-tz
                    const nowUTC = new Date() // Current UTC time
                    const accountTimezone = getTimezoneFromOffset(timezoneOffset)

                    // Convert current UTC time to account timezone
                    const nowInAccountTimezone = toZonedTime(nowUTC, accountTimezone)

                    // Get tomorrow's start of day (midnight) in account timezone
                    const tomorrowMidnightInAccount = startOfDay(addDays(nowInAccountTimezone, 1))

                    // Convert back to UTC using date-fns-tz
                    const midnightUTC = fromZonedTime(tomorrowMidnightInAccount, accountTimezone)
                    scheduleTime = midnightUTC.toISOString()
                } else {
                    // For custom schedule, combine date and time in account timezone
                    const date = reviewLaunchData.launchDate
                    const time = reviewLaunchData.launchTime // Already in 24hr format like "20:00"
                    if (date && time) {
                        // Parse date and time as if they are in account timezone using date-fns-tz
                        const [hours, minutes] = time.split(':').map((num) => parseInt(num, 10))
                        const accountTimezone = getTimezoneFromOffset(timezoneOffset)

                        // Create the scheduled time in account timezone
                        const baseDate = startOfDay(new Date(date))
                        const scheduledInAccount = addMinutes(addHours(baseDate, hours), minutes)

                        // Convert to UTC using date-fns-tz
                        const scheduledUTC = fromZonedTime(scheduledInAccount, accountTimezone)
                        scheduleTime = scheduledUTC.toISOString()
                    }
                }
            }

            // The warm-up features are sent only while they are offered now (not after the objective or the licence
            // changed, or a failed read hid them)
            const warmupOffered = warmupFeaturesOffered({
                objective: String(campaignObjectiveData.selectedObjective || '').toLowerCase(),
                offeredStep: getStepContentData(WARMUP_OFFERED_STEP),
                isAddAudience,
                isEditMode,
                campaignBudget: !!anyCampaignBudgetForSubmission(getStepContentData('budgetSchedule')),
            })
            // Build funnel stages data
            const funnelStages = []
            let leftOutOfBudget = false
            const audienceSelections = salesFunnelData.audienceSelections || {}
            const adCreativeSelections = salesFunnelData.adCreativeSelections || {}

            // Process each stage that has selected audiences
            for (const stageId in audienceSelections) {
                const selectedAudienceIds = audienceSelections[stageId] || []
                if (selectedAudienceIds.length === 0) continue

                const audiencesForStage = []

                // Process each audience in the stage
                for (const audienceId of selectedAudienceIds) {
                    // Get audience display name
                    const audience = audiences.find((a) => a.id === audienceId)
                    if (!audience) continue
                    if (!isAudienceBudgeted(stageId, audienceId)) {
                        leftOutOfBudget = true
                        continue
                    }

                    const displayName = getAudienceDisplayName(stageId, audienceId, audience.name)
                    const budget = getAudienceBudget(stageId, audienceId)

                    // Debug: Verify Website Visitors naming (remove in production)
                    // if (audience.funnel_stage === 'retargeting' && audience.name === 'Website Visitors') {
                    //     console.log('Website Visitors submission name:', {
                    //         stageId,
                    //         audienceId,
                    //         displayName,
                    //         originalName: audience.name
                    //     })
                    // }

                    // Get ad creative IDs for this audience
                    const adCreativeIds = []
                    const creativeKey = `${stageId}_${audienceId}`
                    if (adCreativeSelections[creativeKey]) {
                        adCreativeIds.push(...adCreativeSelections[creativeKey])
                    }

                    // Get targeting settings
                    const locationData = getAudienceLocationData(stageId, audienceId)
                    const genderTargets = getAudienceGenderTargets(stageId, audienceId)
                    const ageTargets = getAudienceAgeTargets(stageId, audienceId)

                    // For submission, get raw device targets from settings directly (not mapped for display)
                    const audienceTargetingData = getStepContentData('audienceTargeting')
                    const stageSettings = audienceTargetingData?.settings?.[stageId]
                    const rawDeviceTargets = stageSettings?.[audienceId]?.device_targets || [
                        'desktop',
                        'mobile_ios',
                        'mobile_android',
                    ]

                    const launchEvent = getAudienceLaunchEvent(stageId, audienceId)
                    const eventChoice = launchEvent.automatic ? 'automatic' : null
                    // An automatic audience keeps its stored event (the server replaces it); a dropped pick launches the default
                    const customEventType = launchEvent.automatic ? launchEvent.stored : launchEvent.event
                    const recencyDays = getAudienceRecencyDays(stageId, audienceId)

                    const settings = {
                        included_countries: locationData.included.map((countryName) => {
                            // Convert country names back to codes
                            const countryData = Object.values(country.all).find((c) => c.name === countryName)
                            return countryData ? countryData.iso2 : countryName
                        }),
                        excluded_countries: locationData.excluded.map((countryName) => {
                            // Convert country names back to codes
                            const countryData = Object.values(country.all).find((c) => c.name === countryName)
                            return countryData ? countryData.iso2 : countryName
                        }),
                        gender_targets: genderTargets || [],
                        age_targets: ageTargets || [],
                        device_targets: rawDeviceTargets || [],
                        placement_targets: getAudiencePlatformPlacementTargets(stageId, audienceId) || [],
                    }

                    // Add custom_event_type if it exists
                    if (customEventType) {
                        settings.custom_event_type = customEventType
                    }

                    // Add recency_days if it exists (for Facebook Fans)
                    if (recencyDays !== null && recencyDays !== undefined) {
                        settings.recency_days = recencyDays
                    }

                    const locales = localesForSubmission(getAudienceLocales(audienceTargetingData, stageId, audienceId))
                    if (locales) settings.locales = locales

                    const exclusions = exclusionsForSubmission(
                        audienceTargetingData,
                        stageId,
                        campaignObjectiveData.selectedObjective,
                    )
                    if (exclusions) settings.exclusions = exclusions

                    audiencesForStage.push(
                        withAccountAudiencePayload(
                            withHypothesisPayload(
                                {
                                    audience_id: audienceId,
                                    name: displayName,
                                    budget: Math.round((budget ?? 0) * 100), // Convert to cents
                                    ad_creative_ids: adCreativeIds,
                                    settings,
                                    ...(getAudienceLimits(stageId, audienceId) || {}),
                                    ...(eventChoice ? { event_choice: eventChoice } : {}),
                                },
                                { ...getHypothesisMap(salesFunnelData), ...getLookalikeMap(salesFunnelData) },
                            ),
                            getAccountAudienceMap(salesFunnelData),
                            stageId,
                        ),
                    )
                }

                if (audiencesForStage.length > 0) {
                    funnelStages.push({
                        stage: stageId,
                        audiences: audiencesForStage,
                    })
                }
            }

            if (!(isEditMode && editAdCampaignId) && leftOutOfBudget && funnelStages.length === 0) {
                showNotification('Select at least one audience with a budget to launch', 'error')
                return false
            }

            // Build the mutation data
            const mutationData = {
                name: campaignName,
                launch_type: launchType,
                schedule_time: scheduleTime,
                objective: campaignObjectiveData.selectedObjective || 'awareness',
                location_targeting: locationTargetingData.selectedLocation || 'worldwide',
                funnel_stages: funnelStages,
            }

            // One campaign budget (Advantage+ leads) instead of audience budgets, or null
            const campaignBudget = anyCampaignBudgetForSubmission(getStepContentData('budgetSchedule'))

            // Call the appropriate mutation based on flow type
            if (isEditMode && editAdCampaignId) {
                // Build ad set update list from funnel stages
                const adSetUpdates = []
                for (const stageId in audienceSelections) {
                    const selectedAudienceIds = audienceSelections[stageId] || []
                    for (const audienceId of selectedAudienceIds) {
                        const mappingKey = `${stageId}_${audienceId}`
                        const adSetId = adSetMapping[mappingKey]
                        if (!adSetId) continue

                        const audience = audiences.find((a) => a.id === audienceId)
                        const displayName = audience
                            ? getAudienceDisplayName(stageId, audienceId, audience.name)
                            : ''

                        const budget = getAudienceBudget(stageId, audienceId)
                        const locationData = getAudienceLocationData(stageId, audienceId)
                        const genderTargets = getAudienceGenderTargets(stageId, audienceId)
                        const ageTargets = getAudienceAgeTargets(stageId, audienceId)
                        const audienceTargetingData = getStepContentData('audienceTargeting')
                        const stageSettings = audienceTargetingData?.settings?.[stageId]
                        const rawDeviceTargets = stageSettings?.[audienceId]?.device_targets || ['desktop', 'mobile_ios', 'mobile_android']
                        const placementTargets = getAudiencePlatformPlacementTargets(stageId, audienceId)
                        const customEventType = getAudienceCustomEventType(stageId, audienceId)
                        const recencyDays = getAudienceRecencyDays(stageId, audienceId)

                        const settings = {
                            included_countries: locationData.included.map((countryName) => {
                                const countryData = Object.values(country.all).find((c) => c.name === countryName)
                                return countryData ? countryData.iso2 : countryName
                            }),
                            excluded_countries: locationData.excluded.map((countryName) => {
                                const countryData = Object.values(country.all).find((c) => c.name === countryName)
                                return countryData ? countryData.iso2 : countryName
                            }),
                            gender_targets: genderTargets || [],
                            age_targets: ageTargets || [],
                            device_targets: rawDeviceTargets || [],
                            placement_targets: placementTargets || [],
                        }

                        if (customEventType) settings.custom_event_type = customEventType
                        if (recencyDays !== null && recencyDays !== undefined) settings.recency_days = recencyDays

                        const locales = localesForSubmission(
                            getAudienceLocales(audienceTargetingData, stageId, audienceId),
                            { keepEmpty: (editPrefetched.localeAdSetKeys || []).includes(mappingKey) },
                        )
                        if (locales) settings.locales = locales

                        const trimmedDisplayName = displayName.trim()
                        // The budget the edit was opened with: the server then never sets it again over a budget
                        // that changed since, for example by a budget move
                        const openedBudget = editPrefetched.adSetBudgets?.[mappingKey]
                        adSetUpdates.push({
                            ad_set_id: adSetId,
                            ...(trimmedDisplayName ? { name: trimmedDisplayName } : {}),
                            // Audiences funded by the campaign budget have no budget of their own
                            ...(isPlainCampaignBudget(getStepContentData('budgetSchedule'))
                                ? spendLimitEditFields(
                                      getStepContentData('budgetSchedule').adSetLimits?.[mappingKey],
                                      editPrefetched.adSetLimits?.[mappingKey],
                                  )
                                : {}),
                            ...(campaignBudget
                                ? {}
                                : {
                                      budget: Math.round(budget * 100),
                                      ...(Number.isFinite(openedBudget) ? { expected_budget: openedBudget } : {}),
                                  }),
                            settings,
                        })
                    }
                }

                lastUpdateWasAdSetOnlyRef.current = isAdSetOnlyEdit

                const updateData = isAdSetOnlyEdit
                    ? { ad_sets: adSetUpdates }
                    : {
                          name: campaignName,
                          ad_sets: adSetUpdates,
                      }
                await updateAdCampaignMutation({
                    variables: {
                        adCampaignId: editAdCampaignId,
                        // The campaign budget is edited through the same update, only when it was changed in the edit
                        data: campaignBudget
                            ? {
                                  ...updateData,
                                  ...campaignBudgetEditFields(
                                      campaignBudget.daily_budget,
                                      editPrefetched.campaignDailyBudget,
                                  ),
                              }
                            : updateData,
                    },
                })
                return true
            } else if (isAddAudience) {
                // Get adCampaignId from prefetched data
                const prefetchedData = getStepContentData('prefetchedData') || {}
                const adCampaignId = params.ad_campaign_id

                if (!adCampaignId) {
                    throw new Error('Ad Campaign ID not found for add audience flow')
                }

                // AI audiences are funded by daily budget moved from one of the campaign's ad sets, once confirmed
                const budgetMove = budgetMoveForSubmission(salesFunnelData, getStepContentData(BUDGET_MOVE_STEP))
                if (budgetMove && !budgetMove.confirmed) {
                    showNotification(
                        'Confirm the budget move before adding the audiences',
                        'error',
                        3000,
                        null,
                        null,
                        true,
                        true,
                    )
                    return false
                }

                await addAudienceToAdCampaign({
                    variables: {
                        publisherId,
                        adCampaignId,
                        data: budgetMove ? { ...mutationData, budget_move: budgetMove } : mutationData,
                    },
                })
            } else {
                // The optional warm-up and maximum daily budget; without a choice nothing is added
                const warmupChoice = warmupOffered ? getStepContentData(WARMUP_STEP) : null
                const objective = String(campaignObjectiveData.selectedObjective || 'awareness').toLowerCase()
                const warmupProblem = warmupChoice
                    ? describeWarmupProblem(warmupChoice, {
                          audienceAmounts: funnelStages.flatMap((stage) =>
                              stage.audiences.map((audience) => audience.budget / 100),
                          ),
                          minDailyBudget: publisherData?.min_daily_budget,
                          objective,
                      })
                    : null
                if (warmupProblem) {
                    showNotification(warmupProblem, 'error', 5000, null, null, true, true)
                    return false
                }
                const warmup = warmupChoice ? warmupForSubmission(warmupChoice, objective) : null
                const maxDailyBudget = warmupChoice ? maxDailyBudgetForSubmission(warmupChoice) : null
                await createAdCampaign({
                    variables: {
                        publisherId,
                        campaignId,
                        data: {
                            ...mutationData,
                            ...(campaignBudget || {}),
                            ...(warmup ? { warmup } : {}),
                            ...(maxDailyBudget ? { max_daily_budget: maxDailyBudget } : {}),
                        },
                    },
                })
            }

            return true
        } catch (error) {
            console.error('Error creating ad campaign:', error)
            const message =
                error?.message ||
                error?.graphQLErrors?.[0]?.message ||
                'Something went wrong while saving. Please try again.'
            showNotification(message, 'error', 5000, null, null, true, true)
            return false
        }
    }, [
        campaignId,
        connectedPublishers,
        getStepContentData,
        audiences,
        getAudienceDisplayName,
        getAudienceBudget,
        isAudienceBudgeted,
        getAudienceLocationData,
        getAudienceCustomEventType,
        getAudienceLaunchEvent,
        getAudienceLimits,
        getAudienceRecencyDays,
        createAdCampaign,
        addAudienceToAdCampaign,
        updateAdCampaignMutation,
        isAddAudience,
        isEditMode,
        editAdCampaignId,
        adSetMapping,
    ])

    const formatTimeLeft = useCallback((date, time) => {
        const now = new Date()
        let endTime = new Date()
        endTime.setHours(24, 0, 0, 0) // Next midnight

        if (date && time) {
            endTime = new Date(`${date}T${time}:00`)
        }

        const duration = intervalToDuration({ start: new Date(), end: endTime })

        const formatted = formatDuration(duration, {
            format: ['days', 'hours', 'minutes'],
        })

        const prefix = isFuture(endTime) ? 'in ' : ''
        return `${prefix}${formatted}`
    }, [])

    // Handle custom navigation with validation
    const handleNextNavigation = useCallback(async () => {
        setIsFormSubmitting(true)
        try {
            const isValid = await validateForm()
            if (isValid) {
                clearAllSettings()
            }
        } finally {
            setIsFormSubmitting(false)
        }
    }, [validateForm, clearAllSettings])

    // Stable ref so registerCustomNavigation effect does not depend on handleNextNavigation identity.
    const handleNextNavigationRef = useRef(handleNextNavigation)
    handleNextNavigationRef.current = handleNextNavigation

    // Register custom navigation with submit button
    useLayoutEffect(() => {
        const launchOption = reviewLaunchData.launchOption

        const CustomNav = () => {
            const { goBack, currentStep } = useWizard()

            return (
                <div className="d-flex gap-2">
                    {currentStep > 0 && (
                        <Button
                            variant="secondary"
                            onClick={goBack}
                            disabled={isSubmitting}
                            className="tw:w-[156px] tw:!text-sm"
                        >
                            Back
                        </Button>
                    )}
                    <SubmitButton
                        buttonText={
                            isEditMode || isEditAdSetMode
                                ? 'Save Changes'
                                : launchOption === 'launch_now'
                                  ? 'Launch'
                                  : 'Schedule'
                        }
                        onClick={() => void handleNextNavigationRef.current()}
                        showSpinner={isSubmitting}
                        spinner="left"
                        className="tw:max-h-9"
                        disabled={isSubmitting}
                    />
                </div>
            )
        }

        registerCustomNavigation(() => <CustomNav />)
    }, [
        registerCustomNavigation,
        isSubmitting,
        reviewLaunchData.launchOption,
        isEditMode,
        isEditAdSetMode,
    ])

    useEffect(() => {
        return () => clearCustomNavigation()
    }, [clearCustomNavigation])

    return {
        // Data
        reviewLaunchData,
        isEditAdSetMode,
        audiences,
        allStages,
        availableStages,
        visibleStages,
        collapsedStages,
        selectedAudience,

        // Loading states
        audiencesLoading,
        creativesLoading,
        isLoading: audiencesLoading || creativesLoading,

        // Methods
        updateReviewLaunchData,
        getSelectedAudiences,
        getCreativesForAudience,
        toggleStageCollapse,
        areAllStagesCollapsed,
        toggleAllStages,
        handleAudienceSelection,
        handleAudienceNameChange,
        getAudienceDisplayName,
        getAudienceBudget,
        usesCampaignBudget,
        usesAutomaticPlacements,
        getAudienceLimits,
        getCampaignObjective,
        getAudienceGenderTargets,
        getAudienceAgeTargets,
        getAudienceDeviceTargets,
        getAudienceLocationData,
        getAudiencePlatformPlacementTargets,
        getAudienceCustomEventType,
        getAudienceLaunchEvent,
        formatTimeLeft,
    }
}

export default useReviewLaunch
